<script lang="ts">
  import { onDestroy } from 'svelte';
  import { ChartAdapter } from '../lib/highcharts-adapter';
  import type { ChartModel, PlotKind } from '../lib/types';

  export let model: ChartModel;
  export let kind: PlotKind;
  export let cursor: number | undefined = undefined;
  export let selectedEventTimestamp: number | undefined = undefined;
  export let compact = false;
  export let showTitle = true;
  export let showSubtitle = true;
  export let onRangeChange: ((range: [number, number]) => void) | undefined = undefined;
  export let onCursorPreview: ((cursor: number) => void) | undefined = undefined;
  export let synchronizedRange: [number, number] | undefined = undefined;
  export let resetZoomVisible = false;
  export let onResetZoom: (() => void) | undefined = undefined;
  export let zoomEnabled: boolean | undefined = undefined;

  let container: HTMLDivElement;
  let adapter: ChartAdapter | null = null;
  let error = '';
  let renderGeneration = 0;
  let signature = '';

  // Adjacent panels such as the histogram can change this width without a
  // window resize, so keep Highcharts synchronized with its actual container.
  function reflowWhenContainerResizes(node: HTMLDivElement) {
    if (typeof ResizeObserver === 'undefined') return {};

    let frame: number | undefined;
    const observer = new ResizeObserver(() => {
      if (frame !== undefined) return;
      frame = window.requestAnimationFrame(() => {
        frame = undefined;
        adapter?.reflow();
      });
    });
    observer.observe(node);

    return {
      destroy() {
        observer.disconnect();
        if (frame !== undefined) window.cancelAnimationFrame(frame);
      }
    };
  }

  async function render(nextSignature: string) {
    if (!container || signature === nextSignature) return;
    signature = nextSignature;
    const generation = ++renderGeneration;
    error = '';
    adapter ??= new ChartAdapter(container);
    try {
      await adapter.render({ model, kind, cursor, showTitle, showSubtitle, onRangeChange, onCursorPreview, onResetZoom, resetZoomVisible, zoomEnabled }, () => generation === renderGeneration);
      if (generation === renderGeneration && synchronizedRange) adapter.setRange(synchronizedRange);
      if (generation === renderGeneration) adapter.setSelectedEvent(selectedEventTimestamp);
    } catch (reason) {
      if (generation === renderGeneration) error = reason instanceof Error ? reason.message : 'Unable to render chart';
    }
  }

  // The explicit container dependency is required: bind:this is assigned after
  // the first legacy reactive flush, so a newly mounted chart must rerun then.
  $: if (container) void render(JSON.stringify([kind, model, showTitle, showSubtitle, Boolean(onRangeChange), Boolean(onCursorPreview), Boolean(onResetZoom), resetZoomVisible, zoomEnabled, document.documentElement.dataset.theme]));
  $: if (adapter && cursor != null) adapter.setCursor(cursor);
  $: if (adapter) adapter.setSelectedEvent(selectedEventTimestamp);
  $: if (adapter && synchronizedRange) adapter.setRange(synchronizedRange);

  function themeChanged() {
    signature = '';
    void render(JSON.stringify([kind, model, showTitle, showSubtitle, Boolean(onRangeChange), Boolean(onCursorPreview), Boolean(onResetZoom), resetZoomVisible, zoomEnabled, document.documentElement.dataset.theme]));
  }

  if (typeof window !== 'undefined') window.addEventListener('pq-theme-change', themeChanged);
  onDestroy(() => {
    renderGeneration += 1;
    adapter?.destroy();
    window.removeEventListener('pq-theme-change', themeChanged);
  });
</script>

<div class:chart-host--compact={compact} class="chart-host">
  <div bind:this={container} use:reflowWhenContainerResizes class="chart-host__canvas" role="region" aria-label={model.title}></div>
  {#if error}<p class="panel-message panel-message--error" role="alert">{error}</p>{/if}
</div>
